import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PropertySetMode } from '../../../property-sets-dialog/property-sets-dialog';
import { Column } from '@miq/catalog';
import { Table } from 'primeng/table';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { forkJoin, map, Observable, startWith, take } from 'rxjs';
import { GroupPermissionFields, PermissionType, UserPermissionFields } from '../permission';
import { PropertySetsHttpService } from '../../../services/http/property-sets-http.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10nTranslationService } from 'angular-l10n';
import { CbreAlertService } from '@cbre/alerts';
import { PermissionTableHelperService } from '../permission-table-helper.service';

@UntilDestroy()
@Component({
  selector: 'miq-permission-table',
  templateUrl: './permission-table.component.html',
  styleUrls: ['./permission-table.component.scss'],
  standalone: false,
})
export class PermissionTableComponent implements OnInit, AfterViewChecked {
  @ViewChild('dataTable') table: Table;
  @Input() title: string;
  @Input() headerColumns: Partial<Column>[] = [];
  @Input() selectionPageOnly = false;
  @Input() columnResizeMode = 'expand';
  @Input() selectionMode = 'single';
  @Input() resizableColumns = 'false';
  @Input() id: number | string = '';
  @Input() hideCheckbox: boolean;
  @Input() isAddDisabled = false;
  @Input() addTooltip = '';
  @Input() sortField = '';
  @Input() sortOrder;
  @Input() propertySetId: number;
  @Output() doneEvent = new EventEmitter();
  @Output() deleteEvent = new EventEmitter();
  @Output() updateEvent = new EventEmitter();
  @Output() sortEvent = new EventEmitter();
  @Output() pageChange = new EventEmitter();
  editingRowId: number | null = null;
  disableSave = false;

  @Input() tableRecords: Record<string, any>[];

  PropertySetMode = PropertySetMode;
  public mode = PropertySetMode.View;

  isEditing = false;
  isNewRowAdded = false;

  private _selectedItems: Record<string, any>[] = [];
  public originalData: Record<string, any> = {};

  addForm: FormGroup;
  userFormControl = new FormControl();
  filteredUsers$: Observable<any[]>;

  constructor(
    private fb: FormBuilder,
    private http: PropertySetsHttpService,
    private translationService: L10nTranslationService,
    private cbreAlertService: CbreAlertService,
    public permissionTableHelperService: PermissionTableHelperService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initializeForm();

    if (this.title === PermissionType.Group) {
      this.loadGroupColumnHeaderValues();
    }

    if (this.title === PermissionType.User) {
      this.loadUserColumnHeaderValues();
    }

    this.filteredUsers$ = this.userFormControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterUsers(value))
    );
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  initializeForm() {
    const formGroup = {};
    this.headerColumns.forEach(column => {
      formGroup[column.field] = new FormControl('');
    });
    this.addForm = this.fb.group(formGroup);

    this.userFormControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.disableSave = !this.userFormControl.value;
    });
  }

  private _filterUsers(value: string) {
    const filterValue = value?.toLowerCase();
    const usersColumn = this.headerColumns.find(column => column.field === UserPermissionFields.UserName);
    if (usersColumn && usersColumn.values) {
      return usersColumn.values.filter(
        option =>
          option.firstName.toLowerCase().includes(filterValue) ||
          option.lastName.toLowerCase().includes(filterValue) ||
          this.usernameValue(option.firstName, option.lastName).toLowerCase().includes(filterValue) ||
          option.emailAddress.toLowerCase().includes(filterValue)
      );
    }
  }

  getDropdownOptionValue(field: string) {
    if (
      field === GroupPermissionFields.PermissionLevel ||
      field === GroupPermissionFields.ConfidentialityLevel ||
      field === GroupPermissionFields.PropertyType
    ) {
      return 'key';
    } else {
      return 'value';
    }
  }

  onOptionSelected(event: any): void {
    const selectedOption = event.option.value;
    this.filteredUsers$
      .pipe(
        map(users => users.find(user => this.usernameValue(user.firstName, user.lastName) === selectedOption)),
        take(1)
      )
      .subscribe(selectedUser => {
        if (selectedUser) {
          const editingUser = this.tableRecords.find(record => record.id === this.editingRowId);
          if (editingUser) {
            editingUser['user_email'] = selectedUser.emailAddress;
          }
        }
      });
  }

  resetSelectedItems(field: string) {
    this.headerColumns.forEach(column => {
      if (column.field === field && this.selectedItems.length === 1) {
        const selectedItem = this.selectedItems[0];
        if (selectedItem[column.field]) {
          ['market', 'region', 'division'].forEach(field => {
            if (selectedItem[`${field}_id`] === 0) {
              selectedItem[`${field}_desc`] = 'All';
            }
          });
          selectedItem[column.field] = column.values[0]?.key;
        }
      }
    });
  }

  onSelectionChange(event: any, col: Column): void {
    const divisionCol = this.headerColumns.find(column => column.field === GroupPermissionFields.Division);
    const marketCol = this.headerColumns.find(column => column.field === GroupPermissionFields.Market);
    const defaultRegion = 'Americas';
    if (col.field === GroupPermissionFields.GlobalRegion) {
      if (event.value === defaultRegion) {
        if (divisionCol) divisionCol.isDisabled = false;
      } else {
        if (divisionCol) divisionCol.isDisabled = true;
        if (marketCol) marketCol.isDisabled = true;
        this.resetSelectedItems(GroupPermissionFields.Division);
        this.resetSelectedItems(GroupPermissionFields.Market);
      }
    }

    if (col.field === GroupPermissionFields.Division) {
      if (event.value === 'All') {
        if (marketCol) marketCol.isDisabled = true;
        this.resetSelectedItems(GroupPermissionFields.Market);
      } else {
        if (marketCol) marketCol.isDisabled = false;
        const selectedDivisionKey = divisionCol?.values?.find(division => division.value === event.value)?.key;
        this.getMarkets(selectedDivisionKey);
      }
    }
  }

  add() {
    this.isEditing = true;
    this.addForm.reset();
    const newRow = this.generateRowData();
    if (this.tableRecords?.length >= 0) {
      newRow.id = -1;
      this.tableRecords = [newRow, ...this.tableRecords];
    }
    this.editingRowId = newRow.id;
    this.table.initRowEdit(newRow);
    this.isNewRowAdded = true;
  }

  generateRowData(): Record<string, any> {
    const newRow = {};
    this.headerColumns.forEach(col => {
      if (col.inputType === 'Text') {
        newRow[col.field] = '';
      } else if (col.inputType === 'dropdown' && col.values && col.values.length > 0) {
        let newRowValue = col.values[0].key;
        if (col.field === GroupPermissionFields.Role) {
          // select US Researcher by default.
          // NOTE: didn't use length-1 as it could cause a bug if the options order changes
          const defaultIndex = col.values.findIndex(option => option.value.toLowerCase().includes('research'));
          newRowValue = col.values[defaultIndex !== -1 ? defaultIndex : 0].value;
        } else if (col.field === GroupPermissionFields.GlobalRegion) {
          // select Americas by default
          // NOTE: didn't use 1 as it could cause a bug if the options order changes
          const defaultIndex = col.values.findIndex(option => option.value.toLowerCase().includes('americas'));
          newRowValue = col.values[defaultIndex !== -1 ? defaultIndex : 0].value;
        }
        newRow[col.field] = newRowValue;
      } else {
        newRow[col.field] = null;
      }
    });
    return newRow;
  }

  cancel() {
    this.isEditing = false;
    if (this.isNewRowAdded) {
      const index = this.tableRecords.findIndex(row => row.id === this.editingRowId);
      if (index !== -1) {
        this.tableRecords.splice(index, 1); // Remove the newly added row
      }
      this.isNewRowAdded = false;
    } else {
      const rowToCancel = this.tableRecords?.find(row => row.id === this.editingRowId);
      if (rowToCancel) {
        // Restore original values
        Object.keys(this.originalData).forEach(key => {
          rowToCancel[key] = this.originalData[key];
        });
        this.table.cancelRowEdit(rowToCancel);
      }
    }
    this.editingRowId = null;
  }

  edit() {
    this.isEditing = true;
    const selectedItem = this.selectedItems[0];
    const divisionId = this.getDivisionId(selectedItem);
    if (divisionId) {
      this.getMarkets(divisionId);
    }
    const marketCol = this.headerColumns.find(column => column.field === GroupPermissionFields.Market);
    const divisionCol = this.headerColumns.find(column => column.field === GroupPermissionFields.Division);
    const isRegionDisabled = selectedItem.region_id !== 1;
    const isDivisionDisabled = selectedItem.division_id === 0;
    if (marketCol) marketCol.isDisabled = isRegionDisabled || isDivisionDisabled;
    if (divisionCol) divisionCol.isDisabled = isRegionDisabled;

    setTimeout(() => {
      this.table.initRowEdit(selectedItem);
    }, 300);

    this.editingRowId = selectedItem.id;
    this.originalData = { ...selectedItem };
  }

  deletePermission() {
    this.deleteEvent.emit(this.selectedItems);
  }

  getRowElement(rowIndex: number): HTMLTableRowElement | null {
    const rows = this.table.el.nativeElement.querySelectorAll('tr');
    return rows[rowIndex] || null;
  }

  done() {
    const newRow = this.tableRecords?.find(record => record.id === this.editingRowId);

    if (!newRow) {
      this.cancel();
      return;
    }

    const savePayload = this.generateSavePayload(newRow);
    const isGroup = savePayload.type === 'Group';
    const existingRows = this.tableRecords.filter(record => record.id !== this.editingRowId);

    if (isGroup) {
      existingRows.forEach(row => {
        row.market_desc = row.market_id === 0 ? 'All' : row.market_desc;
        row.region_desc = row.region_id === 0 ? 'All' : row.region_desc;
        row.division_desc = row.division_id === 0 ? 'All' : row.division_desc;
      });

      const fieldsToCheck = Object.keys(newRow).filter(field => field !== 'id');
      const duplicateRow = existingRows.some(record =>
        fieldsToCheck.every(field => record[field] === savePayload[field])
      );

      if (duplicateRow) {
        this.showDuplicateError(savePayload.type);
        return;
      }
    } else {
      const duplicateRow = this.tableRecords.some(
        record => record.user_name === newRow.user_name && record.id !== this.editingRowId
      );

      if (duplicateRow) {
        this.showDuplicateError(savePayload.type);
        return;
      }
    }

    if (this.editingRowId === -1) {
      this.doneEvent.emit([savePayload]);
    } else {
      this.handleUpdate(savePayload);
    }
  }

  private showDuplicateError(type: string) {
    this.isEditing = true;
    this.cbreAlertService.showErrorSnackBar(
      this.translationService.translate('propertySet.recordExists').replace('{{type}}', type)
    );
  }

  private handleUpdate(savePayload: any) {
    ['market', 'region', 'division'].forEach(field => {
      if (this.originalData[`${field}_id`] === 0) {
        this.originalData[`${field}_desc`] = 'All';
      }
    });

    if (JSON.stringify(savePayload) === JSON.stringify(this.originalData)) {
      this.cancel();
      this.cbreAlertService.showErrorSnackBar(this.translationService.translate('propertySet.noChanges'));
      return;
    }

    this.updateEvent.emit([savePayload]);
  }

  generateSavePayload(newRow) {
    const permissionType = ['Group', 'Groups'].includes(this.title) ? 'Group' : 'User';
    const outputObject = { ...newRow, type: permissionType, entity_id: this.propertySetId };

    for (const key in newRow) {
      if (key.endsWith('_desc') || key.endsWith('_email')) {
        const fieldName = key;
        const idFieldName = key.replace(/_(desc|email)$/, '_id');
        const fieldValue = newRow[fieldName];

        let mappingFieldName = fieldName;
        if (key.endsWith('_email')) {
          mappingFieldName = 'user_name';
        }

        const mappingEntry = this.headerColumns.find(col => col.field === mappingFieldName);

        if (mappingEntry && mappingEntry.values) {
          if (mappingFieldName === 'user_name') {
            const valueEntry = mappingEntry.values.find(user => user.emailAddress === fieldValue);
            if (valueEntry) {
              outputObject[idFieldName] = valueEntry.id;
            }
          } else {
            const valueEntry = mappingEntry.values.find(val => val.value === fieldValue);

            if (valueEntry) {
              outputObject[idFieldName] = valueEntry.key;
            }
          }
        }
      } else if (key === GroupPermissionFields.PropertyType) {
        const fieldValue = newRow[key];
        // should not send 0 on payload.
        outputObject[key] = fieldValue === 0 ? 0 : fieldValue;
      }
    }
    return outputObject;
  }

  private loadGroupColumnHeaderValues() {
    forkJoin({
      role: this.http.getRoles(),
      globalRegions: this.http.getGlobalRegions(),
      division: this.http.getDivision(1),
      market: this.http.getMarkets(0),
      propertyType: this.http.getPropertyType(),
      confidentialityLevel: this.http.getConfidentialityLevel(),
    }).subscribe(({ role, globalRegions, division, market, propertyType, confidentialityLevel }) => {
      const roleColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.Role);
      if (roleColumn) {
        roleColumn.values = role;
      }

      const regionDescColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.GlobalRegion);
      if (regionDescColumn) {
        regionDescColumn.values = globalRegions;
      }

      const divisionDescColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.Division);
      if (divisionDescColumn) {
        divisionDescColumn.values = division;
      }

      const marketDescColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.Market);
      if (marketDescColumn) {
        marketDescColumn.values = market;
      }

      const propertyTypeColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.PropertyType);
      if (propertyTypeColumn) {
        propertyTypeColumn.values = propertyType;
      }

      const confidentialityLevelColumn = this.headerColumns.find(
        column => column.field === GroupPermissionFields.ConfidentialityLevel
      );
      if (confidentialityLevelColumn) {
        confidentialityLevelColumn.values = confidentialityLevel;
      }
    });
  }

  private loadUserColumnHeaderValues() {
    this.http.getActiveUsers().subscribe(activeUsers => {
      const userNameColumn = this.headerColumns.find(column => column.field === UserPermissionFields.UserName);
      if (userNameColumn) {
        userNameColumn.values = activeUsers;
      }
    });
  }

  usernameValue(firstName: string, lastName: string): string {
    return `${firstName} ${lastName}`;
  }

  get selectedItems(): Record<string, any>[] {
    return this._selectedItems;
  }

  set selectedItems(value: Record<string, any>[]) {
    this._selectedItems = value;
    if (this._selectedItems.length === 0) {
      this.isEditing = false;
    }
  }

  getMarkets(divisionId: number): void {
    this.http.getMarkets(divisionId).subscribe(markets => {
      const marketColumn = this.headerColumns.find(column => column.field === GroupPermissionFields.Market);
      if (marketColumn) {
        marketColumn.values = markets;
      }
    });
  }

  getDivisionId(record: Record<string, any>): number {
    return record.division_id;
  }

  clearUsernameInput() {
    this.userFormControl.setValue('');
    const editingUser = this.tableRecords.find(record => record.id === this.editingRowId);
    if (editingUser) {
      editingUser['user_email'] = '';
    }
  }
}
